import { memo, useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
   Button,
   darkenColor,
   Div,
   Dropdown,
   Form,
   getFormErrorObject,
   Icon,
   InputField,
   lightenColor,
   Loader,
   PageHeader,
   Text,
   useBetterHtmlContext,
   useBooleanState,
   useForm,
   useTheme,
   type DropdownOption,
} from "react-better-html";

import { propertyScrapeMinImagesAmount, propertyScrapeTimeout } from "../../../constants/app";

import { type Listing, type ListingScrapeStateStep } from "../../../types/listing";
import { type Property, type PropertyFull, type PropertyFullWithScrapeData } from "../../../types/property";
import { type Workspace } from "../../../types/workspace";

import { urlIsValid } from "../../../utils/functions";
import { useI18n } from "../../../utils/i18n";
import { useReduxAction, useReduxState, useSelectedOrganization, useSSEStream } from "../../../utils/hooks";

import StarsAnimationDecoration from "../../../components/decorations/StarsAnimationDecoration";
import PropertyCard from "../../../components/properties/PropertyCard";

const loadingBarSteps = [1, 2, 3];

const spinnerBorderWidth = 3;
const blobOpacity = 0.4;

const scrapingDotSize = 10;

const addToRoot = "root";

type ScrapingStepProps = {
   scrapingStep?: ListingScrapeStateStep;
   scrapedTotalImages: number;
   scrapedImages: number;
};

const ScrapingStep = memo(function ScrapingStep({
   scrapingStep,
   scrapedTotalImages,
   scrapedImages,
}: ScrapingStepProps) {
   const theme = useTheme();
   const { t } = useI18n();

   const blobColor = lightenColor(theme.colors.primary, 0.2);

   return (
      <Div.column position="relative" width="100%" height="100%" alignItems="center" justifyContent="center">
         <Div position="absolute" top={0} left={0} width="100%" height="100%" zIndex={1}>
            <Icon
               name="blob01"
               position="absolute"
               color={blobColor}
               size={420}
               top={-120}
               right={-120}
               opacity={blobOpacity}
               animation="blobAnimation 9s ease-in-out 0s infinite normal none running"
            />
            <Icon
               name="blob02"
               position="absolute"
               color={blobColor}
               size={260}
               bottom={-20}
               left={-100}
               opacity={blobOpacity}
               animation="blobAnimation 11s ease-in-out 0s infinite reverse none running"
            />
            <Icon
               name="blob03"
               position="absolute"
               color={blobColor}
               size={140}
               bottom={140}
               left={60}
               opacity={blobOpacity}
               animation="blobAnimation 7s ease-in-out 0s infinite normal none running"
            />
            <Icon
               name="blob04"
               position="absolute"
               color={blobColor}
               size={180}
               top={200}
               right={20}
               opacity={blobOpacity}
               animation="blobAnimation 8s ease-in-out 0s infinite reverse none running"
            />
         </Div>

         <StarsAnimationDecoration position="absolute" top={0} left={0} zIndex={1} opacity={0.7} />

         <Div.column
            position="relative"
            width="100%"
            alignItems="center"
            justifyContent="center"
            paddingBottom={40}
            zIndex={2}
         >
            <Div.row
               position="relative"
               alignItems="center"
               justifyContent="center"
               width={90}
               height={90}
               borderRadius={999}
               marginBottom={theme.styles.space * 2}
            >
               <Icon name="trash" color={theme.colors.base} size={32} />

               <Div
                  position="absolute"
                  top={0}
                  left={0}
                  width="100%"
                  height="100%"
                  borderRadius={999}
                  border={`solid 3px ${theme.colors.base}40`}
               >
                  <Div
                     position="absolute"
                     top={-spinnerBorderWidth}
                     left={-spinnerBorderWidth}
                     width={`calc(100% + ${spinnerBorderWidth * 2}px)`}
                     height={`calc(100% + ${spinnerBorderWidth * 2}px)`}
                     borderRadius={999}
                     border={`solid 3px ${theme.colors.base}`}
                     borderLeftColor="transparent"
                     borderRightColor="transparent"
                     borderBottomColor="transparent"
                     animation="spinAnimation 1.4s linear infinite"
                  />

                  <Div
                     position="absolute"
                     top={0}
                     left={0}
                     width="100%"
                     height="100%"
                     backgroundColor={theme.colors.base + "40"}
                     borderRadius={999}
                     animation="pulsAnimation 2s ease-in-out infinite"
                  />
                  <Div
                     position="absolute"
                     top={0}
                     left={0}
                     width="100%"
                     height="100%"
                     backgroundColor={theme.colors.base + "40"}
                     borderRadius={999}
                     animation="pulsAnimation 2s 1s ease-in-out infinite"
                  />
               </Div>
            </Div.row>

            <Text fontSize={24} fontWeight={700} textAlign="center" color={theme.colors.base}>
               {t((l) => l.pages.addProperty.scraping.steps[scrapingStep ?? "FETCHING"].title)}
            </Text>
            <Text textAlign="center" color={theme.colors.base + "a0"}>
               {t((l) => l.pages.addProperty.scraping.steps[scrapingStep ?? "FETCHING"].description)}
            </Text>

            {scrapedTotalImages > 0 && (
               <Text fontSize={14} textAlign="center" color={theme.colors.base + "a0"}>
                  {scrapedImages} / {scrapedTotalImages}
               </Text>
            )}

            <Div.row alignItems="center" gap={theme.styles.gap} marginTop={theme.styles.space * 2}>
               <Div
                  width={scrapingDotSize}
                  height={scrapingDotSize}
                  backgroundColor={theme.colors.base}
                  opacity={
                     scrapingStep === "FETCHING" ||
                     scrapingStep === "READING" ||
                     scrapingStep === "STRUCTURING" ||
                     scrapingStep === "FINALIZING"
                        ? 1
                        : 0.4
                  }
                  borderRadius={999}
                  transition={theme.styles.transition}
               />
               <Div
                  width={scrapingDotSize}
                  height={scrapingDotSize}
                  backgroundColor={theme.colors.base}
                  opacity={
                     scrapingStep === "READING" || scrapingStep === "STRUCTURING" || scrapingStep === "FINALIZING"
                        ? 1
                        : 0.4
                  }
                  borderRadius={999}
                  transition={theme.styles.transition}
               />
               <Div
                  width={scrapingDotSize}
                  height={scrapingDotSize}
                  backgroundColor={theme.colors.base}
                  opacity={scrapingStep === "STRUCTURING" || scrapingStep === "FINALIZING" ? 1 : 0.4}
                  borderRadius={999}
                  transition={theme.styles.transition}
               />
               <Div
                  width={scrapingDotSize}
                  height={scrapingDotSize}
                  backgroundColor={theme.colors.base}
                  opacity={scrapingStep === "FINALIZING" ? 1 : 0.4}
                  borderRadius={999}
                  transition={theme.styles.transition}
               />
            </Div.row>
         </Div.column>
      </Div.column>
   );
});

function AddProperty() {
   const theme = useTheme();
   const { t } = useI18n();
   const { app } = useBetterHtmlContext();
   const location = useLocation();
   const navigate = useNavigate();
   const selectedOrganization = useSelectedOrganization();

   const comingFromPathname = location.state?.comingFromPathname;
   const comingFromWorkspaceId = comingFromPathname
      ? comingFromPathname.split("/")[1] === "workspaces"
         ? comingFromPathname.split("/")[2]
         : undefined
      : undefined;

   const workspacesList = useReduxState((state) => state.workspaces.list);

   const createProperty = useReduxAction((actions) => actions.properties.createProperty);
   const addPropertyToWorkspace = useReduxAction((actions) => actions.properties.addPropertyToWorkspace);
   const getPropertyById = useReduxAction((actions) => actions.properties.getPropertyById);
   const getWorkspacePropertyById = useReduxAction((actions) => actions.properties.getWorkspacePropertyById);

   const scraperTimeoutTimerRef = useRef<NodeJS.Timeout | null>(null);
   const gotScrapedPropertyByIdRef = useRef<boolean>(false);

   const [loadingBarStep, setLoadingBarStep] = useState<number>(1);
   const [listingId, setListingId] = useState<Listing["id"]>();
   const [propertyId, setPropertyId] = useState<Property["id"]>();
   const [scrapingStep, setScrapingStep] = useState<ListingScrapeStateStep>();
   const [scrapedTotalImages, setScrapedTotalImages] = useState<number>(0);
   const [scrapedImages, setScrapedImages] = useState<number>(0);
   const [scrapeWithError, setScrapeWithError] = useBooleanState();

   const [createdProperty, setCreatedProperty] = useState<PropertyFull>();

   const { startListener } = useSSEStream();

   const form = useForm({
      defaultValues: {
         url: "",
         addTo: comingFromWorkspaceId ?? addToRoot,
      },
      requiredFields: ["url", "addTo"],
      validate: (values) => {
         const errors = getFormErrorObject(values);

         if (!urlIsValid(values.url)) {
            errors.url = t((l) => l.pages.addProperty.fields.urlError);
         }

         return errors;
      },
      onSubmit: (values) => {
         if (values.addTo === addToRoot) {
            createProperty({
               data: {
                  url: values.url,
               },
               onSuccess: (response) => {
                  onSuccessAdd(response);
               },
               onError: () => {
                  setScrapeWithError.setTrue();
               },
            });
         } else {
            addPropertyToWorkspace({
               id: values.addTo,
               data: {
                  url: values.url,
               },
               onSuccess: (response) => {
                  onSuccessAdd(response);
               },
               onError: () => {
                  setScrapeWithError.setTrue();
               },
            });
         }

         function onSuccessAdd(property: PropertyFullWithScrapeData) {
            setPropertyId(property.property.id);

            switch (property.listingScrapeData.scrapeState) {
               case "SUCCEEDED": {
                  setCreatedProperty(property);
                  setLoadingBarStep(3);
                  break;
               }

               case "QUEUED": {
                  setListingId(property.listingScrapeData.listingId);
                  setLoadingBarStep(2);
                  break;
               }

               default: {
                  // TODO: Have a UI flow for already scraped properties
                  break;
               }
            }
         }
      },
   });

   const renderOptionDropdownAddTo = useCallback(
      (option: DropdownOption<string, Workspace>, _: number, isSelected: boolean): React.ReactNode => (
         <Div.row alignItems="center" gap={theme.styles.gap}>
            {option.data && (
               <Div
                  width={20}
                  height={20}
                  borderRadius={999}
                  backgroundColor={option.data.color}
                  border={`solid ${theme.styles.borderWidth}px ${darkenColor(option.data.color, 0.4)}${!isSelected ? "00" : ""}`}
               />
            )}

            {option.label}
         </Div.row>
      ),
      [],
   );
   const renderOptionDividerDropdownAddTo = useCallback(
      (
         previousOption: DropdownOption<string, Workspace> | undefined,
         nextOption: DropdownOption<string, Workspace> | undefined,
      ): React.ReactNode =>
         !previousOption?.data && nextOption?.data ? (
            <Div paddingInline={theme.styles.space} paddingTop={theme.styles.gap} paddingBottom={theme.styles.gap / 2}>
               <Text fontSize={14} color={theme.colors.textSecondary}>
                  {t((l) => l.common.workspaces)}
               </Text>
            </Div>
         ) : undefined,
      [t],
   );
   const onClickCancel = useCallback(() => {
      navigate(-1);
   }, []);
   const onFinishScraping = useEffectEvent(() => {
      if (gotScrapedPropertyByIdRef.current) return;

      if (!propertyId) {
         setScrapeWithError.setTrue();
         return;
      }

      if (scraperTimeoutTimerRef.current) clearTimeout(scraperTimeoutTimerRef.current);

      if (form.values.addTo === addToRoot) {
         getPropertyById({
            id: propertyId,
            onSuccess: (response) => {
               onGetPropertyByIdFinish(response);
            },
            onError: () => {
               setScrapeWithError.setTrue();
            },
         });
      } else {
         getWorkspacePropertyById({
            id: propertyId,
            workspaceId: form.values.addTo,
            onSuccess: (response) => {
               onGetPropertyByIdFinish(response);
            },
            onError: () => {
               setScrapeWithError.setTrue();
            },
         });
      }

      gotScrapedPropertyByIdRef.current = true;

      function onGetPropertyByIdFinish(property: PropertyFull) {
         setCreatedProperty(property);

         setLoadingBarStep(3);
      }
   });
   const onClickReset = useCallback(() => {
      setLoadingBarStep(1);
      setListingId(undefined);
      setPropertyId(undefined);
      setScrapingStep(undefined);
      setScrapedTotalImages(0);
      setScrapedImages(0);
      setCreatedProperty(undefined);
      setScrapeWithError.setFalse();
      if (scraperTimeoutTimerRef.current) clearTimeout(scraperTimeoutTimerRef.current);
      gotScrapedPropertyByIdRef.current = false;

      form.reset();
      form.inputFieldRefs.url?.focus();
   }, []);
   const onClickViewProperty = useCallback(() => {
      navigate(`/properties/${propertyId}`);
   }, [propertyId]);

   const addToDropdownOptions = useMemo<DropdownOption<string, Workspace>[]>(
      () => [
         {
            value: addToRoot,
            label: t((l) => l.pages.properties.title[selectedOrganization ? selectedOrganization.type : "PERSONAL"]),
         },
         ...(workspacesList?.map<DropdownOption<string, Workspace>>((workspace) => ({
            value: workspace.id,
            label: workspace.name,
            data: workspace,
         })) ?? []),
      ],
      [t, selectedOrganization],
   );

   useEffect(() => {
      if (!listingId) return;

      const unsubscribe = startListener("getListingProcessingById", {
         pathParams: [listingId],
         onOpen: () => {
            setScrapingStep("FETCHING");

            scraperTimeoutTimerRef.current = setTimeout(() => {
               onFinishScraping();
            }, propertyScrapeTimeout);
         },
         onMessage: (data) => {
            if (data.imageCount !== undefined && data.imageCount !== null) setScrapedTotalImages(data.imageCount);
            setScrapedImages(data.imagesProcessed);

            switch (data.scrapeState) {
               case "QUEUED": {
                  setScrapingStep("FETCHING");
                  break;
               }

               case "EXTRACTING": {
                  setScrapingStep("READING");
                  break;
               }

               case "UNREADABLE": {
                  setScrapingStep("STRUCTURING");
                  break;
               }

               case "SUCCEEDED": {
                  setScrapingStep("FINALIZING");

                  if (
                     data.imageCount === data.imagesProcessed ||
                     data.imagesProcessed >= propertyScrapeMinImagesAmount
                  ) {
                     onFinishScraping();
                  }
                  break;
               }

               case "FAILED": {
                  setScrapeWithError.setTrue();
                  break;
               }

               default: {
                  break;
               }
            }
         },
         onClose: () => {
            if (scraperTimeoutTimerRef.current) clearTimeout(scraperTimeoutTimerRef.current);
         },
         onError: () => {
            setScrapeWithError.setTrue();
         },
      });

      return () => {
         unsubscribe();
         if (scraperTimeoutTimerRef.current) clearTimeout(scraperTimeoutTimerRef.current);
      };
   }, [startListener, listingId]);

   const inSearchStep = loadingBarStep === 2 && !scrapeWithError;

   const loadingBarGap = theme.styles.gap;

   return (
      <Div className="add-property-page" maxWidth={app.contentMaxWidth / 2} marginInline="auto">
         <Div.row width="100%" height={5} borderRadius={999} gap={loadingBarGap} marginBottom={theme.styles.space}>
            {loadingBarSteps.map((item) => (
               <Div
                  width={`${100 / loadingBarSteps.length}%`}
                  height="100%"
                  backgroundColor={theme.colors.border}
                  borderRadius={999}
                  key={item}
               >
                  <Div
                     width={item <= loadingBarStep ? "100%" : "0%"}
                     height="100%"
                     backgroundColor={theme.colors.primary}
                     borderRadius={999}
                     transition={theme.styles.transition}
                  />
               </Div>
            ))}
         </Div.row>

         <Div.box
            height={inSearchStep ? 560 : undefined}
            backgroundImage={
               inSearchStep ? `linear-gradient(to top, ${theme.colors.secondary}, ${theme.colors.primary})` : undefined
            }
            border="none"
            borderRadius={theme.styles.borderRadius * 2}
            boxShadow={theme.styles.boxShadow}
            overflow={loadingBarStep === 2 ? "hidden" : undefined}
            padding={theme.styles.space * 2}
         >
            {!scrapeWithError ? (
               loadingBarStep === 1 ? (
                  <>
                     <PageHeader
                        title={t((l) => l.pages.addProperty.title)}
                        titleAs="h2"
                        description={t((l) => l.pages.addProperty.description)}
                     />

                     <Form form={form}>
                        <Div.column width="100%" gap={theme.styles.gap}>
                           <InputField
                              label={t((l) => l.pages.addProperty.fields.url)}
                              type="url"
                              autoFocus
                              autoComplete="off"
                              placeholder="https://imot.bg/..."
                              backgroundColor={theme.colors.backgroundBase}
                              {...form.getInputFieldProps("url")}
                           />

                           <Dropdown
                              options={addToDropdownOptions}
                              label={t((l) => l.pages.addProperty.fields.addTo)}
                              withoutClearButton
                              backgroundColor={theme.colors.backgroundBase}
                              renderOption={renderOptionDropdownAddTo}
                              renderOptionDivider={renderOptionDividerDropdownAddTo}
                              {...form.getDropdownFieldProps("addTo")}
                           />
                        </Div.column>

                        <Button
                           width="100%"
                           type="submit"
                           loaderName="properties:createProperty"
                           backgroundColor={theme.colors.textPrimary}
                           marginTop={theme.styles.space * 2}
                           text={t((l) => l.pages.addProperty.submitButton)}
                        />
                     </Form>
                  </>
               ) : loadingBarStep === 2 ? (
                  <ScrapingStep
                     scrapingStep={scrapingStep}
                     scrapedTotalImages={scrapedTotalImages}
                     scrapedImages={scrapedImages}
                  />
               ) : loadingBarStep === 3 ? (
                  createdProperty ? (
                     <Div.column gap={theme.styles.space}>
                        <Div.row alignItems="center" gap={theme.styles.space}>
                           <Div.row
                              alignItems="center"
                              justifyContent="center"
                              width={40}
                              height={40}
                              borderRadius={999}
                              backgroundColor={lightenColor(theme.colors.success, 0.7)}
                           >
                              <Icon name="check" color={theme.colors.success} size={20} />
                           </Div.row>

                           <PageHeader
                              title={t((l) => l.pages.addProperty.scraping.finish.title)}
                              titleAs="h2"
                              description={t((l) => l.pages.addProperty.scraping.finish.description)}
                              marginBottom={0}
                           />
                        </Div.row>

                        <PropertyCard property={createdProperty} />

                        <Div.row alignItems="center" gap={theme.styles.gap} marginTop={theme.styles.gap}>
                           <Button.secondary
                              flex={1}
                              backgroundColor={theme.colors.backgroundBase}
                              text={t((l) => l.pages.addProperty.actions.trackAnother)}
                              onClick={onClickReset}
                           />
                           <Button
                              flex={1}
                              backgroundColor={theme.colors.textPrimary}
                              text={t((l) => l.pages.addProperty.actions.viewProperty)}
                              onClick={onClickViewProperty}
                           />
                        </Div.row>
                     </Div.column>
                  ) : (
                     <Loader.box />
                  )
               ) : undefined
            ) : (
               <>
                  <Div.row alignItems="center" gap={theme.styles.space} marginBottom={theme.styles.space}>
                     <Div.row
                        alignItems="center"
                        justifyContent="center"
                        width={40}
                        height={40}
                        borderRadius={999}
                        backgroundColor={lightenColor(theme.colors.error, 0.7)}
                     >
                        <Icon name="XMark" color={theme.colors.error} size={20} />
                     </Div.row>

                     <PageHeader
                        title={t((l) => l.pages.addProperty.failed.title)}
                        titleAs="h2"
                        description={t((l) => l.pages.addProperty.failed.description)}
                        marginBottom={0}
                     />
                  </Div.row>

                  <Div.row alignItems="center" gap={theme.styles.gap}>
                     <Button
                        flex={1}
                        backgroundColor={theme.colors.textPrimary}
                        text={t((l) => l.pages.addProperty.actions.tryAgain)}
                        onClick={onClickReset}
                     />
                  </Div.row>
               </>
            )}
         </Div.box>

         <Div.row alignItems="center" justifyContent="center" marginTop={theme.styles.space}>
            <Button.text
               icon="arrowLeft"
               iconSize={12}
               text={t((l) => l.pages.addProperty.backButton)}
               fontWeight={400}
               color={theme.colors.textSecondary}
               onClick={onClickCancel}
            />
         </Div.row>
      </Div>
   );
}

export default memo(AddProperty);
